import React from 'react';
import './App.css';
import './styles/owner-upgrade.css';
import './styles/cms.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from './components/ui/toaster';
import { Toaster as Sonner } from './components/ui/sonner';
import { SiteProvider } from './context/SiteContext';
import { LocaleProvider, useLocale } from './context/LocaleContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { CookieConsent, MaintenanceGate } from './components/PublicWebsite';
import OwnerDashboardPage from './pages/owner/OwnerDashboardPage';
import OwnerSubscriptionsPage from './pages/owner/OwnerSubscriptionsPage';
import OwnerActivityPage from './pages/owner/OwnerActivityPage';
import OwnerCurrenciesPage from './pages/owner/OwnerCurrenciesPage';
import OwnerPartnersPage from './pages/owner/OwnerPartnersPage';
import PartnerPage from './pages/staff/PartnerPage';
import PartnerRegisterPage from './pages/staff/PartnerRegisterPage';
import OwnerWebsiteEditorPage from './pages/owner/OwnerWebsiteEditorPage';
import OwnerMediaPage from './pages/owner/OwnerMediaPage';
import OwnerSettingsPage from './pages/owner/OwnerSettingsPage';
import OwnerPagesPage from './pages/owner/OwnerPagesPage';
import PublicPage from './pages/PublicPage';
import ProtectedRoute from './components/ProtectedRoute';
import AuthCallback from './components/AuthCallback';
import LoginPage from './pages/LoginPage';
import SetupPasswordPage from './pages/SetupPasswordPage';
import OwnerShopsPage from './pages/owner/OwnerShopsPage';
import OwnerReportPage from './pages/owner/OwnerReportPage';
import OwnerStaffAllowancePage from './pages/owner/OwnerStaffAllowancePage';
import DashboardPage from './pages/staff/DashboardPage';
import JournalPage from './pages/staff/JournalPage';
import CustomersListPage from './pages/staff/CustomersListPage';
import AddCustomerPage from './pages/staff/AddCustomerPage';
import { JournalReportPage, BalanceSummaryPage } from './pages/staff/ReportPages';
import AccountStatementPage from './pages/staff/AccountStatementPage';
import ReceiptDetailPage from './pages/staff/ReceiptDetailPage';
import LoansPage from './pages/staff/LoansPage';
import StaffPage from './pages/staff/StaffPage';
import StaffSettingsPage from './pages/staff/SettingsPage';
import CustomerHomePage from './pages/customer/CustomerHomePage';
import RequestMoneyPage from './pages/customer/RequestMoneyPage';
import RequestHistoryPage from './pages/customer/RequestHistoryPage';
import PublicPaymentRequestPage from './pages/customer/PublicPaymentRequestPage';
import QRPayPage from './pages/customer/QRPayPage';
import ScanRequestQRPage from './pages/customer/ScanRequestQRPage';
import CustomerPinSetupPage from './pages/customer/CustomerPinSetupPage';
import ScanCustomerQRPage from './pages/staff/ScanCustomerQRPage';
import MerchantWalletPage from './pages/staff/MerchantWalletPage';
import MerchantReceiptPage from './pages/staff/MerchantReceiptPage';
import SendMoneyPage from './pages/customer/SendMoneyPage';
import HistoryPage from './pages/customer/HistoryPage';
import ProfilePage from './pages/customer/ProfilePage';
import CustomerReceiptPage from './pages/customer/CustomerReceiptPage';
import CustomerStatementPage from './pages/customer/CustomerStatementPage';

const S = (el) => <ProtectedRoute requireKind="staff">{el}</ProtectedRoute>;
const C = (el) => <ProtectedRoute requireKind="customer">{el}</ProtectedRoute>;
const O = (el) => <ProtectedRoute requireKind="owner">{el}</ProtectedRoute>;

function AppRouter() {
  useLocale();
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<PublicPage home />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/pay/request/:token" element={<PublicPaymentRequestPage />} />
      <Route path="/setup-password" element={<SetupPasswordPage />} />
      <Route path="/setup-shop-password" element={<SetupPasswordPage staff />} />

      <Route path="/pages/:slug" element={<PublicPage />} />
      <Route path="/owner" element={O(<OwnerDashboardPage />)} />
      <Route path="/owner/dashboard" element={<Navigate to="/owner" replace />} />
      <Route path="/owner/shops" element={O(<OwnerShopsPage />)} />
      <Route path="/owner/shops/staff" element={O(<OwnerStaffAllowancePage />)} />
      <Route path="/owner/shops/:mode" element={O(<OwnerShopsPage />)} />
      <Route path="/owner/website/pages" element={O(<OwnerPagesPage key="page" />)} />
      <Route path="/owner/website/editor" element={O(<OwnerPagesPage key="editor" />)} />
      <Route path="/owner/website/editor/:pageId" element={O(<OwnerWebsiteEditorPage />)} />
      <Route path="/owner/website/media" element={O(<OwnerMediaPage />)} />
      <Route path="/owner/website/policies" element={O(<OwnerPagesPage key="policy" kind="policy" />)} />
      <Route path="/owner/website/:section" element={O(<OwnerSettingsPage />)} />
      <Route path="/owner/system/:section" element={O(<OwnerSettingsPage />)} />
      <Route path="/owner/system/currencies" element={O(<OwnerCurrenciesPage />)} />
      <Route path="/owner/report" element={O(<OwnerReportPage />)} />
      <Route path="/owner/subscriptions" element={O(<OwnerSubscriptionsPage />)} />
      <Route path="/owner/renewals" element={O(<OwnerSubscriptionsPage renewals />)} />
      <Route path="/owner/system/activity" element={O(<OwnerActivityPage />)} />

      <Route path="/app/dashboard" element={S(<DashboardPage />)} />
      <Route path="/app/scan-qr" element={S(<ScanCustomerQRPage />)} />
      <Route path="/app/merchant" element={S(<MerchantWalletPage />)} />
      <Route path="/app/merchant/receipt/:id" element={S(<MerchantReceiptPage />)} />
      <Route path="/app/journal" element={S(<JournalPage />)} />
      <Route path="/app/journal/report" element={S(<JournalReportPage />)} />
      <Route path="/app/statement" element={S(<AccountStatementPage />)} />
      <Route path="/app/customers" element={S(<CustomersListPage />)} />
      <Route path="/app/customers/new" element={S(<AddCustomerPage />)} />
      <Route path="/app/loans" element={S(<LoansPage />)} />
      <Route path="/owner/partners" element={O(<OwnerPartnersPage />)} />
      <Route path="/app/partner" element={S(<PartnerPage />)} />
      <Route path="/app/partner/shops" element={S(<PartnerPage section="shops" />)} />
      <Route path="/app/partner/commissions" element={S(<PartnerPage section="commissions" />)} />
      <Route path="/app/partner/register" element={S(<PartnerRegisterPage />)} />
      <Route path="/app/staff" element={S(<StaffPage />)} />
      <Route path="/app/settings" element={S(<StaffSettingsPage />)} />
      <Route path="/app/reports" element={S(<BalanceSummaryPage />)} />
      <Route path="/app/receipts/:id" element={S(<ReceiptDetailPage />)} />

      <Route path="/portal" element={C(<CustomerHomePage />)} />
      <Route path="/portal/send" element={C(<SendMoneyPage />)} />
      <Route path="/portal/receive" element={C(<RequestMoneyPage />)} />
      <Route path="/portal/request" element={C(<RequestMoneyPage />)} />
      <Route path="/portal/requests" element={C(<RequestHistoryPage />)} />
      <Route path="/portal/pay" element={C(<QRPayPage />)} />
      <Route path="/portal/scan-request" element={C(<ScanRequestQRPage />)} />
      <Route path="/portal/pin-setup" element={C(<CustomerPinSetupPage />)} />
      <Route path="/portal/history" element={C(<HistoryPage />)} />
      <Route path="/portal/profile" element={C(<ProfilePage />)} />
      <Route path="/portal/statement" element={C(<CustomerStatementPage />)} />
      <Route path="/portal/receipt/:id" element={C(<CustomerReceiptPage />)} />

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <SiteProvider><AuthProvider><LocaleProvider><CurrencyProvider>
        <BrowserRouter>
          <MaintenanceGate><AppRouter /></MaintenanceGate>
          <CookieConsent />
        </BrowserRouter>
        <Toaster />
        <Sonner richColors position="top-right" />
      </CurrencyProvider></LocaleProvider></AuthProvider></SiteProvider>
    </div>
  );
}

export default App;
